import { http } from "@/apis";
import { signUp } from "@/apis/auth/auth";
import type {
  CreateUserData,
  UpdateUserData,
  User,
  UserFilters,
  UserPasswordResult,
  UserProfileUpdate,
  UserProfileUpdateResult,
  UserSearchHit,
  UserSearchParams,
  UserSearchResponse,
  UserStatusAction,
  UserStatusResult,
} from "@/features/users/schemas/user";
import {
  createUser,
  deleteUser,
  getActiveUsers,
  getAllUsers,
  getUserById,
  getUsersByRole,
  getUsersPaginated,
  getUsersWithFilters,
  updateUser,
} from "@/features/users/services/users";
import { getUserFullName } from "@/features/users/utils/user-utils";
import type { PaginatedResponse } from "@/shared/schemas";

/**
 * Tipos para el servicio
 * @interface UserServiceFilters - Filtros para el servicio
 * @extends {UserFilters}
 * @property {number} page - La página a obtener
 * @property {number} pageSize - El tamaño de la página
 */
export interface UserServiceFilters extends UserFilters {
  page?: number;
  pageSize?: number;
}

/**
 * Interfaz para la respuesta del servicio
 * @interface UserServiceResponse - Respuesta del servicio
 * @template T - Tipo de datos
 * @property {T} data - Los datos
 * @property {boolean} success - true si la operación fue exitosa, false en caso contrario
 * @property {string} message - El mensaje de la operación
 */
export interface UserServiceResponse<T> {
  data: T;
  success: true;
  message?: string;
}

/**
 * Interfaz para el error del servicio
 * @interface UserServiceError - Error del servicio
 * @property {boolean} success - false si la operación fue exitosa, true en caso contrario
 * @property {string} error - El mensaje de error
 * @property {string} code - El código de error
 */
export interface UserServiceError {
  success: false;
  error: string;
  code?: string;
}

/**
 * Tipo para el resultado del servicio
 * @template T - Tipo de datos
 * @type {UserServiceResult<T>} - El resultado del servicio
 */
export type UserServiceResult<T> = UserServiceResponse<T> | UserServiceError;

/**
 * Función para manejar errores del servicio
 * @param error - El error a manejar
 * @returns {UserServiceError} El error manejado
 */
function handleServiceError(error: unknown): UserServiceError {
  if (error instanceof Error) {
    return {
      success: false,
      error: error.message,
      code: "SERVICE_ERROR",
    };
  }

  return {
    success: false,
    error: "Error desconocido en el servicio",
    code: "UNKNOWN_ERROR",
  };
}

/**
 * Servicio para crear un usuario completo (auth + firestore)
 * @param userData - Los datos del usuario a crear
 * @param password - La contraseña del usuario
 * @returns {Promise<UserServiceResult<User>>} El resultado del servicio
 */
export async function createUserWithAuth(
  userData: CreateUserData,
  password: string,
  name: string,
  type: "institution" | "physician" | "patient" | "admin" | "super_admin"
): Promise<UserServiceResult<User>> {
  try {
    // 1. Crear usuario en Firebase Auth
    const authResult = await signUp({
      email: userData.email,
      password,
      confirmPassword: password,
      name,
      type,
    });

    if (!authResult.success) {
      return {
        success: false,
        error: authResult.message,
        code: authResult.code,
      };
    }

    // 2. Crear documento en Firestore
    const firestoreUser = await createUser({
      ...userData,
      id: authResult.user.uid, // Usar el UID de Auth
    });

    return {
      data: firestoreUser,
      success: true,
      message: "Usuario creado exitosamente",
    };
  } catch (error) {
    return handleServiceError(error);
  }
}

/**
 * Servicio para obtener usuarios con lógica de negocio
 * @param filters - Los filtros a aplicar
 * @returns {Promise<UserServiceResult<User[] | PaginatedResponse<User>>>} El resultado del servicio
 */
export async function getUsersService(
  filters?: UserServiceFilters
): Promise<UserServiceResult<User[] | PaginatedResponse<User>>> {
  try {
    if (filters?.page && filters.pageSize) {
      // Usar paginación
      const result = await getUsersPaginated(filters.pageSize, filters);

      const paginatedResponse: PaginatedResponse<User> = {
        data: result.users,
        pagination: {
          page: filters.page,
          limit: filters.pageSize,
          total: result.total,
          totalPages: Math.ceil(result.total / filters.pageSize),
        },
        success: true,
      };

      return {
        data: paginatedResponse,
        success: true,
      };
    } else {
      // Usar filtros simples
      const users = await getUsersWithFilters(filters || {});
      return {
        data: users,
        success: true,
      };
    }
  } catch (error) {
    return handleServiceError(error);
  }
}

/**
 * Servicio para obtener estadísticas de usuarios
 * @returns {Promise<UserServiceResult<{ total: number; active: number; inactive: number; byRole: Record<User["role"], number>; }>>} El resultado del servicio
 */
export async function getUserStatsService(): Promise<
  UserServiceResult<{
    total: number;
    active: number;
    inactive: number;
    byRole: Record<User["type"], number>;
  }>
> {
  try {
    const [allUsers, activeUsers] = await Promise.all([getAllUsers(), getActiveUsers()]);

    const inactive = allUsers.length - activeUsers.length;

    // Calcular usuarios por rol
    const byRole = allUsers.reduce(
      (acc, user) => {
        acc[user.type] = (acc[user.type] || 0) + 1;
        return acc;
      },
      {} as Record<User["type"], number>
    );

    return {
      data: {
        total: allUsers.length,
        active: activeUsers.length,
        inactive,
        byRole,
      },
      success: true,
    };
  } catch (error) {
    return handleServiceError(error);
  }
}

/**
 * Servicio para actualizar usuario con validaciones
 * @param userId - El ID del usuario
 * @param userData - Los datos del usuario a actualizar
 * @returns {Promise<UserServiceResult<User>>} El resultado del servicio
 */
export async function updateUserService(
  userId: string,
  userData: UpdateUserData
): Promise<UserServiceResult<User>> {
  try {
    // Validar que el usuario existe
    const existingUser = await getUserById(userId);
    if (!existingUser) {
      return {
        success: false,
        error: "Usuario no encontrado",
        code: "USER_NOT_FOUND",
      };
    }

    // Validaciones de negocio
    if (userData.email && userData.email !== existingUser.email) {
      // Verificar si el email ya existe
      const usersWithEmail = await getUsersWithFilters({
        search: userData.email,
      });

      const emailExists = usersWithEmail.some(
        (user) => user.email === userData.email && user.uid !== userId
      );

      if (emailExists) {
        return {
          success: false,
          error: "El correo electrónico ya está en uso",
          code: "EMAIL_ALREADY_EXISTS",
        };
      }
    }

    // Actualizar usuario
    const updatedUser = await updateUser(userId, userData);

    return {
      data: updatedUser,
      success: true,
      message: "Usuario actualizado exitosamente",
    };
  } catch (error) {
    return handleServiceError(error);
  }
}

/**
 * Servicio para eliminar usuario con validaciones
 * @param userId - El ID del usuario
 * @returns {Promise<UserServiceResult<void>>} El resultado del servicio
 */
export async function deleteUserService(
  userId: string
): Promise<UserServiceResult<void>> {
  try {
    // Validar que el usuario existe
    const existingUser = await getUserById(userId);
    if (!existingUser) {
      return {
        success: false,
        error: "Usuario no encontrado",
        code: "USER_NOT_FOUND",
      };
    }

    // Validaciones de negocio
    if (existingUser.type === "admin") {
      // Contar administradores
      const admins = await getUsersByRole("admin");
      if (admins.length <= 1) {
        return {
          success: false,
          error: "No se puede eliminar el último administrador",
          code: "LAST_ADMIN",
        };
      }
    }

    // Eliminar usuario
    await deleteUser(userId);

    return {
      data: undefined,
      success: true,
      message: "Usuario eliminado exitosamente",
    };
  } catch (error) {
    return handleServiceError(error);
  }
}

/**
 * Servicio para buscar usuarios con lógica avanzada
 * @param searchTerm - El término de búsqueda
 * @param filters - Los filtros a aplicar
 * @returns {Promise<UserServiceResult<User[]>>} El resultado del servicio
 */
export async function searchUsersService(
  searchTerm: string,
  filters?: UserFilters
): Promise<UserServiceResult<User[]>> {
  try {
    if (!searchTerm.trim()) {
      return {
        data: [],
        success: true,
      };
    }

    // Buscar por nombre, email o rol
    const searchFilters: UserFilters = {
      ...filters,
      search: searchTerm,
    };

    const users = await getUsersWithFilters(searchFilters);

    // Ordenar por relevancia (exact matches primero)
    const sortedUsers = users.sort((a, b) => {
      const aFullName = getUserFullName(a).toLowerCase();
      const bFullName = getUserFullName(b).toLowerCase();
      const aNameMatch = aFullName.includes(searchTerm.toLowerCase());
      const bNameMatch = bFullName.includes(searchTerm.toLowerCase());
      const aEmailMatch = a.email.toLowerCase().includes(searchTerm.toLowerCase());
      const bEmailMatch = b.email.toLowerCase().includes(searchTerm.toLowerCase());

      // Priorizar coincidencias exactas
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      if (aEmailMatch && !bEmailMatch) return -1;
      if (!aEmailMatch && bEmailMatch) return 1;

      return 0;
    });

    return {
      data: sortedUsers,
      success: true,
    };
  } catch (error) {
    return handleServiceError(error);
  }
}

/**
 * Servicio para obtener usuarios por rol con estadísticas
 * @param role - El rol a buscar
 * @returns {Promise<UserServiceResult<{ users: User[]; count: number }>>} El resultado del servicio
 */
export async function getUsersByRoleService(
  role: User["type"]
): Promise<UserServiceResult<{ users: User[]; count: number }>> {
  try {
    const users = await getUsersByRole(role);

    return {
      data: {
        users,
        count: users.length,
      },
      success: true,
    };
  } catch (error) {
    return handleServiceError(error);
  }
}

// ---------------------------------------------------------------------------
// Nuevos servicios — API viewmed-users-services
// ---------------------------------------------------------------------------

type ApiSuccess<T> = { success: true; message: string; data: T };

/**
 * Busca usuarios en Algolia.
 * Reemplaza la búsqueda directa en Firestore cuando hay un término de búsqueda.
 */
export async function searchUsersApi(
  params: UserSearchParams
): Promise<UserSearchResponse> {
  const response = await http.get<ApiSuccess<UserSearchResponse>>(
    "/api/v1/users/search",
    {
      q: params.q,
      ...(params.type && { type: params.type }),
      ...(params.category && { category: params.category }),
      ...(params.includeUserData !== undefined && {
        includeUserData: params.includeUserData,
      }),
      ...(params.page !== undefined && { page: params.page }),
      ...(params.hitsPerPage !== undefined && { hitsPerPage: params.hitsPerPage }),
      ...(params.sortByActiveImaging !== undefined && {
        sortByActiveImaging: params.sortByActiveImaging,
      }),
      ...(params.sortByCreatedAt !== undefined && {
        sortByCreatedAt: params.sortByCreatedAt,
      }),
      ...(params.active_imaging !== undefined && {
        active_imaging: params.active_imaging,
      }),
    }
  );
  return response.data;
}

/**
 * Cambia el estado de un usuario en Firebase Auth y Firestore.
 * Acciones: "verify" (verifica + habilita), "enable" (habilita), "disable" (deshabilita).
 */
export async function updateUserStatus(
  uid: string,
  action: UserStatusAction
): Promise<UserStatusResult> {
  const response = await http.patch<ApiSuccess<UserStatusResult>>(
    `/api/v1/users/${uid}/status`,
    { action }
  );
  return response.data;
}

/**
 * Actualiza los campos editables del perfil de un usuario en Firestore.
 * Los campos uid, email y created_at son inmutables y serán rechazados por el servidor.
 */
export async function updateUserProfile(
  uid: string,
  profileData: UserProfileUpdate
): Promise<UserProfileUpdateResult> {
  const response = await http.patch<ApiSuccess<UserProfileUpdateResult>>(
    `/api/v1/users/${uid}/profile`,
    profileData
  );
  return response.data;
}

/**
 * Actualiza el email de un usuario en Firebase Auth y Firestore.
 * Asume que la verificación fue realizada previamente.
 */
export async function updateUserEmailApi(
  uid: string,
  newEmail: string
): Promise<{ updated: boolean }> {
  const response = await http.put<ApiSuccess<{ updated: boolean }>>(
    "/api/v1/users/email",
    { uid, newEmail }
  );
  return response.data;
}

/**
 * Genera y asigna una contraseña aleatória de 10 caracteres.
 * La contraseña se retorna una única vez — el caller debe comunicarla al usuario.
 */
export async function assignUserPassword(uid: string): Promise<UserPasswordResult> {
  const response = await http.post<ApiSuccess<UserPasswordResult>>(
    `/api/v1/users/${uid}/password`
  );
  return response.data;
}

/**
 * Envía un correo de restablecimiento de contraseña al usuario.
 * Usa el endpoint del API (POST /api/v1/auth/password-reset/init) con Bearer token.
 */
export async function sendPasswordResetEmail(
  email: string
): Promise<{ success: boolean }> {
  const response = await http.post<ApiSuccess<{ success: boolean }>>(
    "/api/v1/auth/password-reset/init",
    { email }
  );
  return response.data;
}

/**
 * Reenvía el correo de verificación de cuenta.
 * Endpoint público — no requiere Bearer token.
 * Retorna `sent: false` con `reason: "already_verified"` si la cuenta ya está verificada.
 */
export async function resendVerificationEmail(
  email: string
): Promise<{ success: boolean; sent: boolean; reason?: string }> {
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || "";
  const res = await fetch(`${apiUrl}/api/v1/auth/verification/resend`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error((errBody as { message?: string }).message ?? `Error ${res.status}`);
  }

  const body = (await res.json()) as {
    success: boolean;
    data: { sent: boolean; reason?: string };
    message: string;
  };

  return { success: body.success, sent: body.data.sent, reason: body.data.reason };
}

// Re-export para mantener compatibilidad con imports existentes del schema
export type { UserSearchHit, UserSearchParams, UserSearchResponse };
